import { Injectable } from "@angular/core";
import { SoundEffectMap, SoundEffectId, SoundEffectState, SoundEffect, SoundEffectList } from './sound-effect.model';

@Injectable({
    providedIn: 'root'
})
export class SoundEffectService {
    public isMuted: boolean = false;

    // Map of all possible sound effects
    private readonly soundEffectMap: SoundEffectMap = SoundEffectList;

    constructor() {
    }

    /**
     * Create a audio player and add it to sound effect map
     */
    public preloadSoundPlayer(soundId: SoundEffectId) {
        if(typeof Audio == 'undefined') return;

        let sound = this.soundEffectMap[soundId];
        // Do nothing if sound was already loaded (can play will not fire again in case we try to reload)
        if(sound.state != SoundEffectState.DISABLE) return;

        // Create player
        let player = new Audio();
        player.src = sound.url;
        player.muted = this.isMuted;

        // Register player in sound map
        sound.state = SoundEffectState.READY;
        sound.player = player;
    }

    /**
     * Disable sound effect and remove player
     */
    public removeSoundPlayer(soundId: SoundEffectId) {
        // Set state to be remove from dom
        this.soundEffectMap[soundId].state = SoundEffectState.DISABLE;
        this.soundEffectMap[soundId].player = null;
    }

    /**
     * Play a sound which has already been preloaded
     */
    public async playSound(soundId: SoundEffectId, delay: number = 0) {
        let sound = this.soundEffectMap[soundId];
        if(!sound || !sound.player) {
            console.warn("Could not find sound effect or player, it does not exist, yet?", soundId, sound);
            return;
        }

        // Optional delay of play
        if(delay) {
            // Play and the pause (only this way delayed playing works on iOS)
            await this.prepareDelayedPlay(sound.id);
            if(delay) await new Promise<void>((resolve) => { setTimeout(() => resolve(), delay) });
        }

        if(!this.isPlayerStateOk(sound)) return;
        // Reset time and play sound
        sound.player.currentTime = 0;
        sound.player.play();
    }

    /**
     * Prepare sound to be played delayed (without user interaction).
     * This has to be called on direct user interaction (before any async waits)
     */
    public async prepareDelayedPlay(soundId: SoundEffectId) {
        let sound = this.soundEffectMap[soundId];
        if(!sound || sound.state != SoundEffectState.READY) return;
        // Make sure we do not try to prepare the sound again as this might prevent it from playing
        sound.state = SoundEffectState.PREPARED;

        // Wait 50 ms
        new Promise((resolve) => setTimeout(resolve, 50)).then(() => {
            sound.player.pause();
            sound.player.currentTime = 0;
        });
    }

    /**
     * Stop a sound which has already been preloaded
     */
    public stopSound(soundId: SoundEffectId, silent: boolean = false) {
        let sound = this.soundEffectMap[soundId];
        if(!this.isPlayerStateOk(sound, silent)) return;
        // Pause and reset time
        sound.player.pause();
        sound.player.currentTime = 0;
    }

    /**
     * Stop all sounds currently playing
     */
    public stopAllSounds() {
        for(let soundId of Object.keys(this.soundEffectMap)) {
            this.stopSound(this.soundEffectMap[soundId].id, true);
        }
    }

    /**
    * Mute a sound which has already been preloaded
    */
    public muteSound(soundId: SoundEffectId, mute: boolean, silent: boolean = false) {
        let sound = this.soundEffectMap[soundId];
        if(!this.isPlayerStateOk(sound, silent)) return;
        // Mute or unmute sound
        sound.player.muted = mute;
    }

    /**
     * Mute or unmute all sounds currently playing
     */
    public muteAllSounds(mute: boolean) {
        for(let soundId of Object.keys(this.soundEffectMap)) {
            this.muteSound(this.soundEffectMap[soundId].id, mute, true);
        }
    }

    private isPlayerStateOk(sound: SoundEffect, silent: boolean = false): boolean {
        if(!sound || sound.state == SoundEffectState.DISABLE) {
            if(!silent) console.warn("Sound is disabled or not loaded yet (play ignored), state:" + sound);
            return false;
        }

        if(sound.state == SoundEffectState.LOAD || !sound.player) {
            if(!silent) console.warn("Sound not ready to be played yet (play ignored), state:" + sound.state, sound);
            return false;
        }
        return true;
    }
}
